import {
  IFormJBPHPContactChangeRequest,
  IFormJBPHPContactRoles,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { getJbpHpContactRolesCrud } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/getJbpHpContactRolesCrud'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Logic, Operator } from 'core-components/types'
import { getJbpHpContactsCrud } from 'partner360/features/hpContacts/lib'

export const useSalesTeamRequests = (partnerId: string) => {
  const crud = useMemo(
    () => getJbpHpContactsCrud(`cockpit-sales-team-${partnerId}`).clone(),
    [partnerId]
  )
  const rolesScope = useMemo(
    () => ({
      partnerId,
      crud: getJbpHpContactRolesCrud().clone(),
      loaded: false,
    }),
    [partnerId]
  )
  const [rolesState, setRolesState] = useState<{
    partnerId: string
    items: IFormJBPHPContactRoles[]
  }>({ partnerId, items: [] })
  const [state, setState] = useState<{
    partnerId: string
    items: IFormJBPHPContactChangeRequest[]
    loading: boolean
    error: boolean
  }>({ partnerId, items: [], loading: true, error: false })
  const [revision, setRevision] = useState(0)
  const refresh = useCallback(() => setRevision((value) => value + 1), [])
  const fetchPartnerRoles = useCallback(
    async (force = false) => {
      rolesScope.crud.setFilter({
        logic: Logic.AND,
        filters: [{ field: 'PartnerId', operator: Operator.EQUAL, value: partnerId }],
      })
      const roles = (await rolesScope.crud.fetch({ force })).items
      rolesScope.loaded = true
      const items = [...roles].sort(
        (a, b) =>
          (a.SortOrder ?? Number.MAX_SAFE_INTEGER) - (b.SortOrder ?? Number.MAX_SAFE_INTEGER)
      )
      setRolesState((current) => (current.partnerId === partnerId ? { partnerId, items } : current))
      return roles
    },
    [rolesScope, partnerId]
  )

  useEffect(() => {
    let cancelled = false
    setState({ partnerId, items: [], loading: true, error: false })
    setRolesState((current) =>
      current.partnerId === partnerId ? current : { partnerId, items: [] }
    )
    const load = async () => {
      if (!rolesScope.loaded) await fetchPartnerRoles(true)
      if (cancelled) return
      crud.setSort({ field: 'Modified', dir: 'desc' })
      crud.setFilter({
        logic: Logic.AND,
        filters: [{ field: 'PartnerId', operator: Operator.EQUAL, value: partnerId }],
      })
      const items: IFormJBPHPContactChangeRequest[] = []
      let page = 0
      let total = 0
      do {
        crud.setPaging({ page, pageSize: 100 })
        const result = await crud.fetch({ force: true })
        if (cancelled) return
        items.push(...result.items)
        total = result.total
        if (!result.items.length) break
        page += 1
      } while (items.length < total)
      items.sort((a, b) => Number(Boolean(b.LeadPBM)) - Number(Boolean(a.LeadPBM)))
      if (!cancelled) setState({ partnerId, items, loading: false, error: false })
    }
    load().catch(() => {
      if (!cancelled) setState({ partnerId, items: [], loading: false, error: true })
    })
    return () => {
      cancelled = true
    }
  }, [crud, partnerId, revision, fetchPartnerRoles, rolesScope])

  return {
    ...state,
    items: state.partnerId === partnerId ? state.items : [],
    loading: state.partnerId !== partnerId || state.loading,
    crud,
    partnerRoles: rolesState.partnerId === partnerId ? rolesState.items : [],
    refresh,
    fetchPartnerRoles,
  }
}
